import {
  createTheme,
  lighten,
  darken,
  responsiveFontSizes,
} from "@mui/material/styles";

function hexToRgb(hex: string) {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const bigint = parseInt(full, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

export function getMode(hex: string): "light" | "dark" {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.5 ? "dark" : "light";
}

const HEADING_STYLE = { fontWeight: 900, fontStyle: "italic" };

export type ThemeColors = {
  accent: string;
  background: string;
  textPrimary: string;
  textSecondary: string;
};

export function buildTheme(colors: ThemeColors) {
  const mode = getMode(colors.background);
  const paperBackground =
    mode === "dark"
      ? lighten(colors.background, 0.08)
      : darken(colors.background, 0.02);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: colors.accent,
        light: "#ffffff",
      },
      secondary: {
        light: "rgb(230,230,230)",
        main: "rgb(190,190,190)",
        dark: "rgb(150,150,150)",
      },
      background: {
        default: colors.background,
        paper: paperBackground,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
    },
    shape: { borderRadius: 24 },
    typography: {
      fontFamily: [
        "Nunito",
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: HEADING_STYLE,
      h2: HEADING_STYLE,
      h3: HEADING_STYLE,
      h4: HEADING_STYLE,
      h5: HEADING_STYLE,
      h6: HEADING_STYLE,
    },
    components: {
      // In dark mode MUI paints a white "elevation overlay" over every Paper
      // surface (cards, the AppBar, popovers). That washes out the warm tone
      // set here and pulls these surfaces toward a neutral grey, so disable it
      // and let background.paper paint exactly.
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 24,
            fontWeight: 900,
            letterSpacing: "0.02em",
            fontStyle: "italic",
          },
        },
      },
    },
  });

  // Scales h1-h6 down on smaller screens so pages can rely on the variant
  // alone instead of ad-hoc per-page fontSize overrides.
  return responsiveFontSizes(theme);
}
